/*
  <cart-offer> — spend-threshold gift module (see snippets/cart-offer.liquid).

  The element is fully server-rendered; this script only:
    - toggles the choice panel
    - handles the colour dropdown on each tile (swaps image, price and the
      variant the add button will post)
    - adds the chosen gift variant with the hidden `_cart_offer` property
    - removes the gift line (customer action, or automatically when the cart
      drops back under the threshold — the snippet sets data-auto-remove)
    - re-renders the cart via Shopify section rendering, the same way
      product-form.js and cart.js do, then publishes cartUpdate.
*/
if (!customElements.get('cart-offer')) {
  customElements.define(
    'cart-offer',
    class CartOffer extends HTMLElement {
      static autoRemoving = false;

      constructor() {
        super();
        this.panel = this.querySelector('.cart-offer__drawer');
        this.toggle = this.querySelector('.cart-offer__toggle');
        this.errorEl = this.querySelector('.cart-offer__error');
        this.busy = false;

        this.toggle?.addEventListener('click', () => this.setOpen(!this.isOpen));
        this.querySelectorAll('.cart-offer__add[data-variant-id]').forEach((button) =>
          button.addEventListener('click', (event) => this.add(event.currentTarget))
        );
        this.querySelectorAll('.cart-offer__remove[data-line-key]').forEach((button) =>
          button.addEventListener('click', (event) => this.changeLine(event.currentTarget.dataset.lineKey, 0, event.currentTarget))
        );
        this.querySelectorAll('.cart-offer__product').forEach((tile) => this.initTile(tile));
      }

      connectedCallback() {
        const key = this.dataset.autoRemove;
        if (key && !CartOffer.autoRemoving) {
          CartOffer.autoRemoving = true;
          this.changeLine(key, 0).finally(() => {
            CartOffer.autoRemoving = false;
          });
        }
        if (this.dataset.state !== 'redeemed' && this.readStorage(this.storageKey) === '1') {
          this.setOpen(true);
        }
      }

      /* ---- tiles / colour dropdown ---- */

      initTile(tile) {
        const select = tile.querySelector('.cart-offer__select-input');
        if (!select) return;
        const preselect = tile.dataset.selectedVariant;
        if (preselect && select.querySelector(`option[value="${preselect}"]`)) select.value = preselect;
        const remembered = this.readStorage(`${this.storageKey}-${tile.dataset.tile}`);
        if (this.dataset.state !== 'redeemed' && !tile.dataset.sizeMatched && remembered && select.querySelector(`option[value="${remembered}"]:not([disabled])`)) {
          select.value = remembered;
        }
        this.applyOption(tile, select);
        select.addEventListener('change', (event) => {
          /* cart-items / cart-drawer-items listen for bubbling change events and
             treat any target as a quantity input (reads .step, shows an
             "increments of undefined" error). Keep ours inside the module. */
          event.stopPropagation();
          this.applyOption(tile, select);
          this.writeStorage(`${this.storageKey}-${tile.dataset.tile}`, select.value);
        });
      }

      applyOption(tile, select) {
        const option = select.options[select.selectedIndex];
        if (!option) return;
        const image = tile.querySelector('.cart-offer__product-image');
        if (image && option.dataset.image && image.getAttribute('src') !== option.dataset.image) {
          image.setAttribute('src', option.dataset.image);
          image.removeAttribute('srcset');
          if (option.dataset.imageAlt) image.alt = option.dataset.imageAlt;
        }
        const was = tile.querySelector('.cart-offer__product-was');
        if (was && option.dataset.price) was.textContent = option.dataset.price;

        const add = tile.querySelector('.cart-offer__add[data-variant-id]');
        if (add) {
          add.dataset.variantId = option.value;
          const available = option.dataset.available !== 'false';
          add.disabled = !available;
          const label = add.querySelector('.cart-offer__add-label');
          if (label) {
            if (!add.dataset.addLabel) add.dataset.addLabel = label.textContent.trim();
            label.textContent = available ? add.dataset.addLabel : (window.variantStrings?.soldOut || 'Sold out');
          }
        }
      }

      /* ---- open / close ---- */

      get storageKey() {
        return `cart-offer-open-${this.dataset.context}`;
      }

      readStorage(key) {
        try {
          return sessionStorage.getItem(key);
        } catch (e) {
          return null;
        }
      }

      writeStorage(key, value) {
        try {
          sessionStorage.setItem(key, value);
        } catch (e) {
          /* storage unavailable */
        }
      }

      get isOpen() {
        return this.classList.contains('cart-offer--open');
      }

      setOpen(open) {
        if (!this.panel || !this.toggle) return;
        this.classList.toggle('cart-offer--open', open);
        this.panel.toggleAttribute('inert', !open);
        this.toggle.setAttribute('aria-expanded', String(open));
        const label = this.toggle.querySelector('.cart-offer__toggle-label');
        if (label) {
          label.textContent = open ? this.toggle.dataset.labelOpen : this.toggle.dataset.labelClosed;
        }
        this.writeStorage(this.storageKey, open ? '1' : '0');
      }

      /* ---- cart plumbing ---- */

      get cartPage() {
        return this.closest('cart-items');
      }

      get cartDrawer() {
        return this.closest('cart-drawer') || document.querySelector('cart-drawer');
      }

      sectionIds() {
        if (this.cartPage) {
          return this.cartPage.getSectionsToRender().map((section) => section.section);
        }
        const drawer = this.cartDrawer;
        return drawer ? drawer.getSectionsToRender().map((section) => section.id) : [];
      }

      render(parsedState) {
        const isEmpty = parsedState.item_count === 0;
        document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
        document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
        document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);

        if (this.cartPage) {
          this.cartPage.getSectionsToRender().forEach((section) => {
            const container = document.getElementById(section.id);
            if (!container || !parsedState.sections?.[section.section]) return;
            const target = container.querySelector(section.selector) || container;
            target.innerHTML = this.sectionInnerHTML(parsedState.sections[section.section], section.selector);
          });
          return;
        }
        const drawer = this.cartDrawer;
        if (drawer) drawer.renderContents(parsedState);
      }

      sectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }

      setBusy(button, busy) {
        this.busy = busy;
        this.classList.toggle('cart-offer--busy', busy);
        if (!button) return;
        button.classList.toggle('loading', busy);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
        if (busy) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || window.cartStrings?.error || 'Something went wrong. Please try again.';
        this.errorEl.hidden = false;
      }

      async add(button) {
        if (this.busy) return;
        const variantId = Number(button.dataset.variantId);
        if (!variantId) return;
        this.setBusy(button, true);

        const body = JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { _cart_offer: 'true' } }],
          sections: this.sectionIds(),
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body });
          const data = await response.json();
          if (data.status) {
            this.showError(data.description || data.message);
            return;
          }
          this.setOpen(false);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-offer', productVariantId: variantId, cartData: data });
          this.render(data);
        } catch (error) {
          console.error(error);
          this.showError();
        } finally {
          this.setBusy(button, false);
        }
      }

      async changeLine(lineKey, quantity, button) {
        if (this.busy || !lineKey) return;
        this.setBusy(button, true);

        const body = JSON.stringify({
          id: lineKey,
          quantity,
          sections: this.sectionIds(),
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body });
          const data = await response.json();
          if (data.errors) {
            this.showError(data.errors);
            return;
          }
          this.setOpen(false);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-offer', cartData: data });
          this.render(data);
        } catch (error) {
          console.error(error);
          this.showError();
        } finally {
          this.setBusy(button, false);
        }
      }
    }
  );
}
